import type { Order, OverrideRecord } from '../types';

const STORAGE_KEYS = {
  orders: 'oemstream_override_orders',
  overrides: 'oemstream_override_records',
  deleted: 'oemstream_deleted_orders',
  ignored: 'oemstream_ignored_orders',
};

class OverrideDB {
  private getItem<T>(key: string, defaultValue: T): T {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch {
      return defaultValue;
    }
  }

  private setItem<T>(key: string, value: T): void {
    localStorage.setItem(key, JSON.stringify(value));
  }

  // Override orders stored locally
  getOverrideOrders(): Order[] {
    const orders = this.getItem<Order[]>(STORAGE_KEYS.orders, []);
    // Migrate legacy source label to 'live'
    return orders.map(o => ({ ...o, source: (o.source as string) === 'posthog' ? 'live' as const : o.source }));
  }

  saveOverrideOrder(order: Order): void {
    const orders = this.getOverrideOrders();
    const index = orders.findIndex(o => o.id === order.id);
    if (index >= 0) {
      orders[index] = { ...order, source: 'override', updatedAt: new Date().toISOString() };
    } else {
      orders.push({ ...order, source: 'override', updatedAt: new Date().toISOString() });
    }
    this.setItem(STORAGE_KEYS.orders, orders);
  }

  deleteOverrideOrder(orderId: string): void {
    const orders = this.getOverrideOrders().filter(o => o.id !== orderId);
    this.setItem(STORAGE_KEYS.orders, orders);
  }

  // Override records - track which fields were overridden
  getOverrides(): OverrideRecord[] {
    return this.getItem<OverrideRecord[]>(STORAGE_KEYS.overrides, []);
  }

  saveOverride(record: OverrideRecord): void {
    const overrides = this.getOverrides();
    const index = overrides.findIndex(o => o.orderId === record.orderId && o.field === record.field);
    if (index >= 0) {
      overrides[index] = record;
    } else {
      overrides.push(record);
    }
    this.setItem(STORAGE_KEYS.overrides, overrides);
  }

  removeOverride(orderId: string, field: keyof Order): void {
    this.setItem(
      STORAGE_KEYS.overrides,
      this.getOverrides().filter(o => !(o.orderId === orderId && o.field === field))
    );
  }

  /** Import field-level override records (used by config import). */
  importFieldOverrides(records: OverrideRecord[], mode: 'merge' | 'replace'): number {
    if (mode === 'replace') {
      this.setItem(STORAGE_KEYS.overrides, records);
      return records.length;
    }
    let count = 0;
    for (const record of records) {
      this.saveOverride(record);
      count++;
    }
    return count;
  }

  /** Import deleted-order ids (used by config import). */
  importDeletedIds(ids: string[], mode: 'merge' | 'replace'): number {
    if (mode === 'replace') {
      this.setItem(STORAGE_KEYS.deleted, ids);
      return ids.length;
    }
    const merged = [...new Set([...this.getDeletedIds(), ...ids])];
    this.setItem(STORAGE_KEYS.deleted, merged);
    return ids.length;
  }

  // Deleted orders (from live analytics that we want to hide)
  getDeletedIds(): string[] {
    return this.getItem<string[]>(STORAGE_KEYS.deleted, []);
  }

  markDeleted(orderId: string): void {
    const deleted = this.getDeletedIds();
    if (!deleted.includes(orderId)) {
      deleted.push(orderId);
      this.setItem(STORAGE_KEYS.deleted, deleted);
    }
  }

  restoreDeleted(orderId: string): void {
    const deleted = this.getDeletedIds().filter(id => id !== orderId);
    this.setItem(STORAGE_KEYS.deleted, deleted);
  }

  clearDeleted(): void {
    localStorage.removeItem(STORAGE_KEYS.deleted);
  }

  // Apply overrides to a live order
  applyOverrides(order: Order): Order {
    const overrides = this.getOverrides().filter(o => o.orderId === order.id);
    const result = { ...order };
    for (const override of overrides) {
      (result as any)[override.field] = override.value;
    }
    return result;
  }

  // Merge live orders with override orders
  mergeOrders(liveOrders: Order[]): Order[] {
    const overrideOrders = this.getOverrideOrders();
    const deletedIds = this.getDeletedIds();
    const overrideIds = new Set(overrideOrders.map(o => o.id));

    // Filter out deleted and apply overrides to live orders
    const processedLive = liveOrders
      .filter(o => !deletedIds.includes(o.id))
      .filter(o => !overrideIds.has(o.id))
      .map(o => this.applyOverrides(o));

    // Combine with override orders (override takes precedence)
    return [...processedLive, ...overrideOrders];
  }

  // Full CRUD for manual order entry
  createOrder(order: Omit<Order, 'id' | 'createdAt' | 'updatedAt' | 'source'>, createdAt?: string): Order {
    const ts = createdAt || order.sessionStart || new Date().toISOString();
    const newOrder: Order = {
      ...order,
      id: `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: ts,
      updatedAt: new Date().toISOString(),
      source: 'override',
    };
    this.saveOverrideOrder(newOrder);
    return newOrder;
  }

  /** Upsert full orders by id (used by config import — idempotent). */
  importOrders(orders: Order[], mode: 'merge' | 'replace'): number {
    if (mode === 'replace') {
      this.setItem(STORAGE_KEYS.orders, orders.map(o => ({ ...o, source: 'override' as const })));
      return orders.length;
    }
    let count = 0;
    for (const order of orders) {
      this.saveOverrideOrder({ ...order, source: 'override' });
      count++;
    }
    return count;
  }

  updateOrder(orderId: string, updates: Partial<Order>): Order | null {
    const orders = this.getOverrideOrders();
    const index = orders.findIndex(o => o.id === orderId);
    if (index >= 0) {
      orders[index] = { ...orders[index], ...updates, updatedAt: new Date().toISOString() };
      this.setItem(STORAGE_KEYS.orders, orders);
      return orders[index];
    }

    // If it's a live order, create override records
    for (const [key, value] of Object.entries(updates)) {
      this.saveOverride({
        orderId,
        field: key as keyof Order,
        value,
        updatedAt: new Date().toISOString(),
        updatedBy: 'admin',
      });
    }

    return null;
  }

  deleteOrder(orderId: string): void {
    // Remove from overrides if it's a manual order
    this.deleteOverrideOrder(orderId);
    // Mark as deleted if it's from live analytics
    this.markDeleted(orderId);
  }

  restoreOrder(orderId: string): void {
    this.restoreDeleted(orderId);
  }

  clearAll(): void {
    Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
  }
}

export const db = new OverrideDB();
export default OverrideDB;
